import React, { useCallback, useState } from "react"

import { normalizeTag, readRecordTags, writeRecordTags } from "@/tags/values"
import {
  Button,
  focusOnMount,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  toast,
} from "@coval/ui"
import { Tag } from "lucide-react"

import { logger } from "@/lib/logger"
import type { TransportOperationRequest } from "@/types_api/backend/transport/operations"
import type { ConversationsTableResult } from "@/types/tables/ConversationsTable"

import { useConversationUpdateRunTagsTransport } from "../routes/useConversationRunTagsTransport"
import { useInvalidateConversationsResultsTransport } from "../routes/useConversationsResultsTransportInvalidation"

const log = logger("BulkTagDropdown")
const MAX_RUN_TAGS = 20

/**
 * The resource these rows' tags belong to. The endpoint writes a run's whole
 * tag list, so these writes share a queue and ledger with the run table's row
 * and bulk tag editors, which address the same runs by the same ids.
 */
const TAG_RESOURCE = "runs"

type UpdateRunTags = NonNullable<
  TransportOperationRequest<"updateRunTags">["tags"]
>

function toUpdateRunTags(tags: string[]): UpdateRunTags {
  return tags.slice(0, MAX_RUN_TAGS) as UpdateRunTags
}

interface BulkTagDropdownProps {
  selectedKeys: Set<string>
  currentData: ConversationsTableResult[]
  onSuccess?: () => void
}

export const BulkTagDropdown: React.FC<BulkTagDropdownProps> = ({
  selectedKeys,
  currentData,
  onSuccess,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [tagValue, setTagValue] = useState("")
  const [isApplying, setIsApplying] = useState(false)

  const updateTagsMutation = useConversationUpdateRunTagsTransport()
  const invalidateConversationsResultsTransport =
    useInvalidateConversationsResultsTransport()

  const handleApply = useCallback(async () => {
    const trimmed = tagValue.trim()
    if (!trimmed || selectedKeys.size === 0 || isApplying) {
      return
    }

    setIsApplying(true)
    let failCount = 0

    const selectedItems = currentData.filter(
      (item) =>
        item.simulation_output_id &&
        selectedKeys.has(item.simulation_output_id),
    )

    const tag = normalizeTag(trimmed)

    for (const item of selectedItems) {
      // What the run's outstanding writes have set, rather than the row, which
      // lags them — two selected conversations can share a run, and a run tag
      // editor elsewhere can have an edit in flight against the same run.
      const existingTags = readRecordTags(TAG_RESOURCE, item.run_id, item.tags)
      if (existingTags.includes(tag)) {
        continue
      }

      const { error, landed } = await writeRecordTags({
        recordId: item.run_id,
        resource: TAG_RESOURCE,
        tags: [...existingTags, tag],
        updateTags: (tags) =>
          updateTagsMutation.mutateAsync({
            run_id: item.run_id,
            tags: toUpdateRunTags(tags),
          }),
      })

      if (!landed) {
        failCount++
        log.warn("Failed to apply tag", {
          run_id: item.run_id,
          error: error instanceof Error ? error.message : String(error),
        })
      }
    }

    setIsApplying(false)
    setTagValue("")
    setIsOpen(false)
    const invalidationResults = await Promise.allSettled([
      invalidateConversationsResultsTransport(),
    ])
    for (const result of invalidationResults) {
      if (result.status === "rejected") {
        log.warn(
          "Failed to invalidate uploaded conversation rows after tagging",
          {
            error:
              result.reason instanceof Error
                ? result.reason.message
                : String(result.reason),
          },
        )
      }
    }

    if (failCount > 0) {
      toast.danger({
        title: `Failed to tag ${failCount} of ${selectedItems.length} items`,
      })
    } else {
      toast.success({
        title: `Tagged ${selectedItems.length} item${selectedItems.length === 1 ? "" : "s"} with "${tag}"`,
      })
      onSuccess?.()
    }
  }, [
    tagValue,
    selectedKeys,
    currentData,
    updateTagsMutation,
    invalidateConversationsResultsTransport,
    onSuccess,
    isApplying,
  ])

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="secondary"
          disabled={selectedKeys.size === 0}
          aria-label="Add tag to selected rows"
        >
          <Tag className="w-4 h-4" />
          Tag
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-3 w-64" sideOffset={8} align="start">
        <div className="flex flex-col gap-2">
          <Input
            placeholder="Enter a value..."
            value={tagValue}
            onChange={(e) => setTagValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && tagValue.trim()) {
                void handleApply()
              }
            }}
            ref={focusOnMount}
            aria-label="Tag value"
          />
          <Button
            variant="default"
            onClick={() => {
              void handleApply()
            }}
            disabled={!tagValue.trim() || isApplying}
            aria-label="Apply tag"
          >
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
